/** Donor controls over the one native engine. Source and adaptations: TRANSFER.json. */
import { Button } from "@/components/ui/button";
import { DataTableFacetedFilter } from "./donor/data-table-faceted-filter";
import { DataTableViewOptions } from "./donor/data-table-view-options";
import { DataTablePagination } from "./donor/data-table-pagination";
import { facetColumnId, type RecordTableEngine } from "./record-table-engine";

export function RecordTableToolbar<T>({ engine }: { engine: RecordTableEngine<T> }) {
  const showColumns = engine.columnVisibility && engine.hideableColumns.length > 0;
  if (engine.facetBuckets.length === 0 && !showColumns) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-rule px-4 py-2.5">
      {engine.facetBuckets.map(({ facet, options }) => (
        <DataTableFacetedFilter
          key={facet.id}
          column={engine.table.getColumn(facetColumnId(facet.id))!}
          title={facet.label}
          single={facet.single}
          options={options.map((option) => ({ ...option, label: option.label ?? option.value }))}
        />
      ))}
      {engine.activeFacetCount > 0 && (
        <Button type="button" variant="ghost" size="sm" onClick={engine.clearFacets}>Clear filters</Button>
      )}
      {showColumns && (
        <DataTableViewOptions
          table={engine.table}
          options={engine.hideableColumns.map((column) => ({ id: column.id, label: column.name, group: "columns" }))}
        />
      )}
    </div>
  );
}

export function RecordTablePagination<T>({ engine }: { engine: RecordTableEngine<T> }) {
  if (!engine.paginated || (engine.pageCount <= 1 && engine.totalRowCount > 0)) return null;
  return (
    <div data-record-table-footer="" className="flex flex-wrap items-center justify-between gap-3 border-t border-rule py-3 px-4">
      <p className="text-meta text-muted-foreground" aria-live="polite">
        {engine.pageBounds.first}-{engine.pageBounds.last} of {engine.totalRowCount}
      </p>
      <DataTablePagination table={engine.table} />
    </div>
  );
}

export function RecordSelectionBar<T>({ engine, actions }: { engine: RecordTableEngine<T>; actions?: React.ReactNode }) {
  if (!engine.selectable || engine.selectedRowIds.length === 0) return null;
  const visible = new Set(engine.rows.map(engine.rowId));
  const hidden = engine.selectedRowIds.filter((id) => !visible.has(id)).length;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule bg-primary-wash py-2.5 px-4">
      <p className="text-ui font-medium text-foreground" aria-live="polite">
        <span>{engine.selectedRowIds.length} selected</span>{hidden > 0 && <span> · {hidden} hidden</span>}
      </p>
      <div className="flex items-center gap-2">
        {actions}
        <Button type="button" variant="ghost" size="sm" onClick={engine.clearSelection}>Clear</Button>
      </div>
    </div>
  );
}
